import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, SafeAreaView, TouchableOpacity,
  Image, ScrollView, Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  OrganDonorRec,
  organDonorService,
} from '../../../../services/organDonorInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('OrganDonorData');

type OrganDonorDataScreenRouteProp = RouteProp<RootStackParamList, 'OrganDonorData'>;

type RouteParams = {
  organDonorId?: string;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const OrganDonorDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const [organDonorInfo, setOrganDonorInfo] = useState<OrganDonorRec | null>(null);
  const [loading, setLoading] = useState(true);

  const organDonorId = route.params?.organDonorId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadOrganDonorInfo();
  }, [targetUserId]);

  const loadOrganDonorInfo = async () => {
    try {
      if (organDonorId) {
        const info = await organDonorService.getOrganDonorById(organDonorId);
        setOrganDonorInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        // Pass targetUserId to fetch another user's data (for successor view)
        const allInfo = await organDonorService.getAllOrganDonor(targetUserId);
        if (allInfo.length > 0) {
          setOrganDonorInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading organ donor info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (organDonorInfo) {
      // Map the data from organDonorInfo to the format expected by OrganDonorInputScreen
      const mappedData = {
        id: organDonorInfo.id,
        isOrganDonor: organDonorInfo.isOrganDonor,
        isRegisteredDonor: organDonorInfo.isRegisteredDonor,
        interestedInBecomingDonor: organDonorInfo.interestedInBecomingDonor,
        donorRegistry: organDonorInfo.donorRegistry,
        organsTissues: organDonorInfo.organsTissues,
        restrictions: organDonorInfo.restrictions,
        emergencyContact: organDonorInfo.emergencyContact,
        contactPhone: organDonorInfo.contactPhone,
        notes: organDonorInfo.notes,
      };

      navigation.navigate('OrganDonorInput', {
        organDonorData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('OrganDonorInput');
    }
  };

  const handleDelete = () => {
    if (organDonorInfo) {
      Alert.alert(
        'Delete Organ Donor Information',
        'Are you sure you want to delete this organ donor information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await organDonorService.deleteOrganDonor(organDonorInfo.id);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await organDonorService.getAllOrganDonor();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('organ_donation');
                }

                navigation.navigate('MedicalChecklist');
              } catch (error) {
                logger.error('Error deleting organ donor info:', error);
                Alert.alert('Error', 'Failed to delete organ donor information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('MedicalChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Organ Donor Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Organ Donor Details</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {organDonorInfo ? (
            <>
              <InfoItem label="Organ Donor" value={organDonorInfo.isOrganDonor} />

              <InfoItem label="Registered Donor" value={organDonorInfo.isRegisteredDonor} />

              <InfoItem label="Interested in Becoming Donor" value={organDonorInfo.interestedInBecomingDonor} />

              {organDonorInfo.donorRegistry && (
                <InfoItem label="Donor Registry" value={organDonorInfo.donorRegistry} />
              )}

              {organDonorInfo.organsTissues && (
                <InfoItem label="Organs/Tissues" value={organDonorInfo.organsTissues} />
              )}

              {organDonorInfo.restrictions && (
                <InfoItem label="Restrictions" value={organDonorInfo.restrictions} />
              )}

              {organDonorInfo.emergencyContact && (
                <InfoItem label="Emergency Contact" value={organDonorInfo.emergencyContact} />
              )}

              {organDonorInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={organDonorInfo.contactPhone} />
              )}

              {organDonorInfo.notes && (
                <InfoItem label="Notes" value={organDonorInfo.notes} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>
                {viewOnly ? 'This user has not added organ donor information yet' : 'No organ donor information found.'}
              </Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Organ Donor Information</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0', // Teal header background
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF', // White title on teal background
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // White content area
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280', // Gray label text
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F', // Dark blue value text
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0', // Teal separator line
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0', // Teal back button
  },
  deleteButton: {
    backgroundColor: '#FF4444', // Red delete button
  },
  editButton: {
    backgroundColor: '#FF9F5A', // Orange edit button
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default OrganDonorDataScreen;